import { forwardRef, useEffect, useState } from 'react';

import { InputText } from '@eneco/sparky';
import { InputTextProps } from '@eneco/sparky/types';

export const isValidCompanyNumber = (value: string | undefined) => {
  if (!value) return false;

  const numberOnly = value?.toLocaleLowerCase().replaceAll('be', '').replaceAll('.', '').trim();
  if (!/^\d{10}$/.test(numberOnly)) return false;

  // CRC checksum
  const base = Number(numberOnly.slice(0, 8));
  const control = Number(numberOnly.slice(8));

  const expectedControl = 97 - (base % 97) || 97;
  return control === expectedControl;
};

type CompanyNumberInputProps = Omit<InputTextProps, 'value' | 'onChange'> & {
  value?: string;
  onChange: (value: string) => void;
};

export const CompanyNumberInput = forwardRef<HTMLInputElement, CompanyNumberInputProps>(
  ({ value: controlledValue, onChange, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState<string>(controlledValue ?? '');

    useEffect(() => {
      setInternalValue(controlledValue ?? '');
    }, [controlledValue]);

    const handleChange = (value: string) => {
      const cleanedValue = value.replace(/\./g, '');
      if (cleanedValue.length > 10) return;
      let formattedValue = cleanedValue;
      // Add the first dot after 4 characters
      if (cleanedValue.length > 4) {
        formattedValue = cleanedValue.slice(0, 4) + '.' + cleanedValue.slice(4);
      }
      // Add the second dot after 7 characters
      if (cleanedValue.length > 7) {
        formattedValue = formattedValue.slice(0, 8) + '.' + formattedValue.slice(8);
      }
      setInternalValue(formattedValue);
      onChange(formattedValue);
    };

    return (
      <InputText ref={ref} value={internalValue} onChange={event => handleChange(event.target.value)} {...props} />
    );
  },
);
